import { AmplitudeRenderer } from '@renderer/utils/amplitude.renderer'

export type SidepanelSource = 'hotkey' | 'edge' | 'trigger' | 'mini'

/** Which handle committed the gesture: the inner edge, a top/bottom edge, or a corner. */
export type SidepanelResizeHandle = 'width' | 'height' | 'corner'

export function trackSidepanelOpened(source: SidepanelSource) {
  AmplitudeRenderer.trackEvent('Sidepanel Opened', { source })
}

export function trackSidepanelClosed(source: SidepanelSource) {
  AmplitudeRenderer.trackEvent('Sidepanel Closed', { source })
}

export interface SidepanelResize {
  handle: SidepanelResizeHandle
  width: number
  previousWidth: number
  height: number
  previousHeight: number
  defaultWidth: number
  defaultHeight: number
  /** Vertical space the panel can occupy (viewport minus an overlapping dock). */
  bandHeight: number
  viewportWidth: number
  viewportHeight: number
}

const percentOf = (value: number, total: number) => (total > 0 ? Math.round((value / total) * 100) : null)

/**
 * Emits one event per committed sidepanel resize. A pointer-down that never
 * moves still commits the same numbers, so unchanged gestures are dropped here
 * rather than at each of the three call sites.
 */
export function trackSidepanelResized({
  handle,
  width,
  previousWidth,
  height,
  previousHeight,
  defaultWidth,
  defaultHeight,
  bandHeight,
  viewportWidth,
  viewportHeight,
}: SidepanelResize) {
  if (width === previousWidth && height === previousHeight) {
    return
  }

  AmplitudeRenderer.trackEvent('Sidepanel Resized', {
    handle,
    width,
    height,
    previousWidth,
    previousHeight,
    defaultWidth,
    defaultHeight,
    widerThanDefault: width > defaultWidth,
    tallerThanDefault: height > defaultHeight,
    widthPercentOfDefault: percentOf(width, defaultWidth),
    heightPercentOfDefault: percentOf(height, defaultHeight),
    widthPercentOfViewport: percentOf(width, viewportWidth),
    heightPercentOfBand: percentOf(height, bandHeight),
    fillsBandHeight: height >= bandHeight,
    bandHeight,
    viewportWidth,
    viewportHeight,
  })
}
